import { useContext } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { TableHead, TableRow } from '@repo/videa-components/src/ui/table';
import classNames from 'classnames';

import { useLocalizedFormatDate } from '@/modules/claims/freeClaims/state';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';

import { isReverseOrderDcm } from './helpers';
import { PerioChartModeContext } from './types';
import type { Arch, PerioChartResponse } from './types';

interface Props {
  arch: Arch;
  examDate?: PerioChartResponse['examDate'];
  toothIds: string[];
}

export const ToothNumberRow = ({ arch, examDate, toothIds }: Props) => {
  const { t } = useLingui();
  const mode = useContext(PerioChartModeContext);
  const formatDate = useLocalizedFormatDate();
  const { displayToothNumber } = useUniversalToFDI();
  const isUpper = arch === 'upper';

  return (
    <TableRow className="vai-border-none">
      <TableHead
        className={classNames(
          'vai-font-inter vai-h-auto vai-text-[12px] vai-flex vai-flex-col vai-gap-[10px] !vai-p-2 !vai-pt-1',
          {
            'vai-text-zinc-300': mode === 'dark',
            'vai-text-zinc-950': mode === 'light',
          },
        )}
      >
        <Trans>F</Trans>
        {!!examDate && (
          <p>
            <Trans>EXAM DATE&#58;</Trans>{' '}
            {examDate ? formatDate(examDate, false, 'PP') : t`N/A`}
          </p>
        )}
      </TableHead>

      {toothIds.map((toothId) => {
        const label = isReverseOrderDcm(toothId) ? t`MCD` : t`DCM`;

        return (
          <TableHead
            className={classNames('vai-h-auto !vai-p-1 !vai-pb-2', {
              'vai-align-bottom': isUpper,
              'vai-align-top': !isUpper,
            })}
            key={toothId}
          >
            <div
              className={classNames(
                'vai-flex vai-flex-col vai-gap-[2px] vai-items-center',
                { 'vai-flex-col-reverse': !isUpper },
              )}
            >
              <div
                className={classNames(
                  'vai-h-5 vai-w-5 vai-flex vai-items-center vai-justify-center vai-rounded-full vai-border',
                  {
                    'vai-bg-zinc-800 vai-border-zinc-600 vai-text-zinc-50':
                      mode === 'dark',
                    'vai-bg-white vai-border-zinc-800 vai-text-zinc-950':
                      mode === 'light',
                  },
                )}
              >
                <p className="vai-font-inter vai-text-xs">
                  {displayToothNumber(toothId)}
                </p>
              </div>
              <p
                className={classNames('vai-font-inter vai-text-[12px]', {
                  'vai-text-zinc-300': mode === 'dark',
                  'vai-text-zinc-950': mode === 'light',
                })}
              >
                {label}
              </p>
            </div>
          </TableHead>
        );
      })}
    </TableRow>
  );
};
